import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Dumbbell, Loader2, Sun, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Sheet } from '@/components/ui/Sheet';
import { storageGet, storageSet, KEYS } from '@/lib/storage';
import { callClaude, formatError } from '@/lib/anthropic';
import { buildCoachSystem, COACH_SUGGESTIONS } from '@/lib/coach';
import { hapticTap } from '@/lib/haptic';
import type { CoachMessage, CoachMode, Profile } from '@/types';

export interface CoachProps {
  profile: Profile;
  weekIdx: number;
  onClose: () => void;
}

export const Coach = ({ profile, weekIdx, onClose }: CoachProps) => {
  const [mode, setMode] = useState<CoachMode | null>(null);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Charge messages persistés au changement de mode
  useEffect(() => {
    if (!mode) return;
    void storageGet<CoachMessage[]>(`${KEYS.coachPrefix}${mode}:messages`, [])
      .then(setMessages);
    setErrorMsg(null);
  }, [mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading || !mode) return;
    const userText = input.trim();
    setInput('');
    setErrorMsg(null);
    hapticTap();
    const newMsgs: CoachMessage[] = [...messages, { role: 'user', content: userText }];
    setMessages(newMsgs);
    setLoading(true);

    const system = buildCoachSystem(profile, weekIdx, mode);
    const result = await callClaude({
      usage: 'coach',
      system,
      messages: newMsgs,
      maxTokens: 1000,
      timeoutMs: 30_000,
      retries: 2,
    });

    if (result.ok) {
      const final: CoachMessage[] = [...newMsgs, { role: 'assistant', content: result.text }];
      setMessages(final);
      void storageSet(`${KEYS.coachPrefix}${mode}:messages`, final);
    } else {
      setErrorMsg(formatError(result.error));
    }
    setLoading(false);
  };

  const onSuggest = (q: string) => {
    setInput(q);
  };

  const doReset = async () => {
    if (!mode) return;
    setMessages([]);
    await storageSet(`${KEYS.coachPrefix}${mode}:messages`, []);
    setConfirmReset(false);
  };

  // Sélection du mode
  if (!mode) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Choisir un coach"
        className="fixed inset-0 bg-zinc-950/95 backdrop-blur-md z-50 flex flex-col pt-safe pb-safe pl-safe pr-safe animate-fade-in"
      >
        <div className="flex justify-between items-center p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500">Coach</div>
          <Button variant="ghost" iconOnly aria-label="Fermer" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-4 max-w-md mx-auto w-full">
          <h2 className="text-2xl font-light mb-2">Quel coach ?</h2>
          <p className="text-sm text-zinc-500 mb-8 text-center">Tu passes de l'un à l'autre à tout moment.</p>

          <button
            onClick={() => setMode('sport')}
            className="w-full mb-3 text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60"
          >
            <Card>
              <div className="flex items-center gap-3">
                <div className="bg-accent-500/10 rounded-full p-2.5">
                  <Dumbbell className="w-5 h-5 text-accent-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Sport &amp; Boxe</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Technique, programmation, prise de masse, récup</div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </div>
            </Card>
          </button>

          <button
            onClick={() => setMode('life')}
            className="w-full text-left rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60"
          >
            <Card>
              <div className="flex items-center gap-3">
                <div className="bg-accent-500/10 rounded-full p-2.5">
                  <Sun className="w-5 h-5 text-accent-400" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Vie &amp; Hygiène</div>
                  <div className="text-xs text-zinc-500 mt-0.5">Sommeil, routines, mindset, présentation, ambition</div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </div>
            </Card>
          </button>
        </div>
      </div>
    );
  }

  const suggestions = COACH_SUGGESTIONS[mode];

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'sport' ? 'Coach Sport' : 'Coach Vie'}
        className="fixed inset-0 bg-zinc-950 z-50 flex flex-col pt-safe pl-safe pr-safe"
      >
        <div className="flex justify-between items-center p-4 border-b border-zinc-800">
          <Button variant="ghost" iconOnly aria-label="Choisir un autre coach" onClick={() => setMode(null)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-xs uppercase tracking-wider text-accent-400">
            {mode === 'sport' ? 'Coach Sport' : 'Coach Vie'}
          </div>
          <Button variant="ghost" iconOnly aria-label="Fermer le coach" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <div className="text-sm text-zinc-500 mb-6">
                {mode === 'sport'
                  ? 'Pose une question sur la technique, la programmation, la nutrition…'
                  : 'Pose une question sur le sommeil, les routines, le mindset…'}
              </div>
              <div className="flex flex-col gap-2 max-w-sm mx-auto">
                {suggestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => onSuggest(q)}
                    className="text-xs text-left px-3 py-2.5 min-h-tap bg-zinc-900 hover:bg-zinc-800 rounded-lg text-zinc-300 border border-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}>
              <div className={[
                'max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap',
                m.role === 'user'
                  ? 'bg-accent-500 text-zinc-950 rounded-br-md'
                  : 'bg-zinc-900 text-zinc-100 rounded-bl-md border border-zinc-800',
              ].join(' ')}>
                {m.content}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start mb-3">
              <div className="bg-zinc-900 px-3.5 py-3 rounded-2xl rounded-bl-md border border-zinc-800 max-w-[85%] w-64">
                <SkeletonLines count={2} />
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="flex justify-start mb-3">
              <div className="bg-rose-900/20 border border-rose-900/50 text-rose-200 px-3.5 py-2.5 rounded-2xl rounded-bl-md text-sm">
                {errorMsg}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-zinc-800 p-3 bg-zinc-950 pb-safe">
          {messages.length > 0 && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setConfirmReset(true)}
                className="text-xs text-zinc-500 hover:text-zinc-300 underline min-h-tap px-2"
              >
                Nouvelle conversation
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !loading) void send(); }}
              placeholder="Écris ta question…"
              disabled={loading}
              aria-label="Message au coach"
              className="flex-1 min-h-tap bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-accent-500"
            />
            <Button variant="primary" onClick={() => void send()} disabled={!input.trim() || loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer'}
            </Button>
          </div>
        </div>
      </div>

      <Sheet
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        title="Effacer la conversation ?"
        centered
      >
        <p className="text-sm text-zinc-400 mb-4">
          La conversation avec le {mode === 'sport' ? 'Coach Sport' : 'Coach Vie'} sera supprimée.
          Cette action est irréversible.
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={() => setConfirmReset(false)}>Annuler</Button>
          <Button variant="danger" fullWidth onClick={() => void doReset()}>Effacer</Button>
        </div>
      </Sheet>
    </>
  );
};
